import { useState, useEffect } from 'react';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { ScrollArea } from '@/components/ui/scroll-area';
import { MessageSquare, FileText, Calendar, Users } from 'lucide-react';
import { supabase } from '@/integrations/supabase/client';
import { toast } from 'sonner';
import { formatDistanceToNow } from 'date-fns';

interface Activity {
  id: string;
  activity_type: string;
  activity_data: any;
  created_at: string;
  profiles?: { first_name: string; last_name: string };
}

export function GroupActivityFeed({ groupId }: { groupId: string }) {
  const [activities, setActivities] = useState<Activity[]>([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    loadActivities();
    
    // Subscribe to real-time updates
    const channel = supabase
      .channel(`group-${groupId}-activities`)
      .on(
        'postgres_changes',
        {
          event: 'INSERT',
          schema: 'public',
          table: 'group_activities',
          filter: `group_id=eq.${groupId}`
        },
        () => loadActivities()
      )
      .subscribe();

    return () => {
      supabase.removeChannel(channel);
    };
  }, [groupId]);

  const loadActivities = async () => {
    setLoading(true);
    try {
      const { data: activitiesData, error } = await supabase
        .from('group_activities')
        .select('*')
        .eq('group_id', groupId)
        .order('created_at', { ascending: false })
        .limit(20);

      if (error) throw error;

      // Fetch profiles separately
      const userIds = activitiesData?.map(a => a.user_id) || [];
      const { data: profilesData } = await supabase
        .from('profiles')
        .select('id, first_name, last_name')
        .in('id', userIds);

      const profileMap = new Map(profilesData?.map(p => [p.id, p]));
      const enriched = activitiesData?.map(a => ({
        ...a,
        profiles: profileMap.get(a.user_id)
      })) || [];

      setActivities(enriched);
    } catch (error) {
      console.error('Error loading activities:', error);
      toast.error('Failed to load activity feed');
    } finally {
      setLoading(false);
    }
  };

  const getActivityIcon = (type: string) => {
    switch (type) {
      case 'discussion_created': return <MessageSquare className="h-4 w-4" />;
      case 'resource_added': return <FileText className="h-4 w-4" />;
      case 'meeting_scheduled': return <Calendar className="h-4 w-4" />;
      case 'member_joined': return <Users className="h-4 w-4" />;
      default: return <FileText className="h-4 w-4" />;
    }
  };

  const getActivityText = (activity: Activity) => {
    const name = `${activity.profiles?.first_name} ${activity.profiles?.last_name}`;
    switch (activity.activity_type) {
      case 'discussion_created':
        return `${name} started a discussion: ${activity.activity_data?.title}`;
      case 'resource_added':
        return `${name} added a resource: ${activity.activity_data?.title}`;
      case 'meeting_scheduled':
        return `${name} scheduled a meeting`;
      case 'member_joined':
        return `${name} joined the group`;
      default:
        return `${name} performed an action`;
    }
  };

  return (
    <Card>
      <CardContent className="p-4">
        <h3 className="font-medium mb-4">Recent Activity</h3>
        <ScrollArea className="h-[400px]">
          <div className="space-y-3">
            {activities.map((activity) => (
              <div key={activity.id} className="flex items-start gap-3 p-2 rounded-lg hover:bg-muted/50">
                <div className="p-2 bg-primary/10 text-primary rounded-full">
                  {getActivityIcon(activity.activity_type)}
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-sm">{getActivityText(activity)}</p>
                  <p className="text-xs text-muted-foreground mt-1">
                    {formatDistanceToNow(new Date(activity.created_at), { addSuffix: true })}
                  </p>
                </div>
              </div>
            ))}
            {activities.length === 0 && !loading && (
              <div className="text-center py-8 text-sm text-muted-foreground">
                No recent activity
              </div>
            )}
          </div>
        </ScrollArea>
      </CardContent>
    </Card>
  );
}